import type { ReactNode } from "react";
import { STUDIO_URL } from "../studio";

export type SiteFlags = Record<string, boolean | string>;

// `flag` hides an item until that feature flag is on. `leavesApp` is for a
// page outside the SPA's router, which has to be a real page load.
const NAV_ITEMS: { page: string; href: string; label: string; flag?: string; leavesApp?: boolean }[] = [
  { page: "create", href: "/create", label: "Text to Embroidery" },
  { page: "convert", href: "/convert", label: "Convert Files", flag: "format_converter" },
  { page: "preview", href: "/preview", label: "Embroidery Viewer", flag: "embroidery_viewer" },
  { page: "replayUpload", href: "/replay", label: "Stitch Simulator", flag: "stitch_replayer" },
  { page: "digitize", href: "/digitize", label: "Digitize SVG", flag: "svg_digitizer" },
  { page: "blog", href: "/blog/", label: "Tutorials", leavesApp: true },
];

// Drawn rather than typed: the ☰/✕ characters sit wherever each platform's
// font puts them, which is visibly off-centre in the button on some devices.
// These paths are symmetric about the middle of the 24x24 box, so the icon is
// centred everywhere.
const MENU_ICON = "M4 6H20M4 12H20M4 18H20";
const CLOSE_ICON = "M6 6L18 18M18 6L6 18";

interface SiteHeaderProps {
  flags: SiteFlags;
  // The page to show as "you are here", or null for none.
  currentPage: string | null;
  navOpen: boolean;
  // Client-side navigation. Without it every item is a plain link.
  onNavigate?: (page: string) => void;
  onToggleNav?: () => void;
  // The bar's avatar, and its phones-only stand-in at the foot of the menu.
  profileMenu?: ReactNode;
  accountSummary?: ReactNode;
  // The Studio link, for a caller that counts clicks on it.
  renderStudioLink?: (children: ReactNode) => ReactNode;
}

/**
 * The site's header, for the app and for the static tutorials in blog/, which
 * render it to HTML at build time and never hydrate it. So it holds no state
 * and can't reach a context: anything live comes in as a prop. The menu button
 * carries its own icons and labels as data attributes so blog/'s small script
 * needs no copy of them.
 */
export function SiteHeader({
  flags,
  currentPage,
  navOpen,
  onNavigate,
  onToggleNav,
  profileMenu,
  accountSummary,
  renderStudioLink,
}: SiteHeaderProps) {
  // A nav item for the page it names renders as plain, non-clickable text
  // instead of a link -- there is nowhere useful for "you are here" to go,
  // and a link to the current page reads as broken rather than as a no-op.
  const navItem = (page: string, href: string, label: string, leavesApp = false) => {
    if (currentPage === page) {
      return (
        <span className="nav-current" aria-current="page">
          {label}
        </span>
      );
    }
    const onClick =
      onNavigate && !leavesApp
        ? (e: React.MouseEvent) => {
            e.preventDefault();
            onNavigate(page);
          }
        : undefined;
    return (
      <a href={href} onClick={onClick}>
        {label}
      </a>
    );
  };

  const studioLabel = (
    <>
      Studio <span className="nav-badge">Beta</span>
    </>
  );

  return (
    <header className="container">
      <nav>
        <ul>
          <li>
            <strong>{navItem("home", "/", "🧵 Embroidery Online")}</strong>
          </li>
        </ul>
        {profileMenu}
        {/* Only visible on phones, where the tool links below would otherwise
            stack into several rows above every page. It sits at the right end
            of the bar, opposite the brand. */}
        <button
          type="button"
          className="nav-toggle"
          aria-expanded={navOpen}
          aria-controls="nav-links"
          aria-label={navOpen ? "Close menu" : "Open menu"}
          data-menu-icon={MENU_ICON}
          data-close-icon={CLOSE_ICON}
          onClick={onToggleNav}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d={navOpen ? CLOSE_ICON : MENU_ICON}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>
        <ul id="nav-links" className={navOpen ? "nav-links open" : "nav-links"}>
          {NAV_ITEMS.filter((item) => !item.flag || flags[item.flag] === true).map((item) => (
            <li key={item.page}>{navItem(item.page, item.href, item.label, item.leavesApp)}</li>
          ))}
          <li className="studio-upsell">
            {renderStudioLink ? renderStudioLink(studioLabel) : <a href={STUDIO_URL}>{studioLabel}</a>}
          </li>
          {accountSummary && <li className="nav-account">{accountSummary}</li>}
        </ul>
      </nav>
    </header>
  );
}
